import firebase from "../../firebase"
import {
  SnapPath,
  ScreenState,
  MetaType,
  TypeArray,
  FirestoreTimestampType
} from "../types"
import {
  actionCreatorGuard,
  actionCreatorTypeGuard,
  reducerGuard,
  processActionMeta,
  getArrayReducerGuard
} from "../reducers"
import { ScreenStateSchema } from "../schemas"

import _ from "lodash"

const kind = "snap_state"

type StudentTotal = {
  totalAttempted: number
  totalQuestions: number
  correct: number
}

export type SnapState = {
  kind: typeof kind
  version: number
  snapPaths: Array<SnapPath>
  screenStates: Array<ScreenState>
  screenIdIndex: Array<string>
  courseId: string
  contentId: string
  snapId: string
  schoolYear: number
  release: number
  completed: boolean
  completed_timestamp: FirestoreTimestampType | null
  seed: number
  meta: MetaType
  studentTotal?: StudentTotal
}

export const reducerAction = {
  meta: actionCreatorGuard<SnapState, MetaType>("meta", kind),
  screenStates: actionCreatorGuard<SnapState, Array<ScreenState>>(
    "screenStates",
    kind
  ),
  screenIdIndex: actionCreatorGuard<SnapState, Array<string>>(
    "screenIdIndex",
    kind
  ),
  courseId: actionCreatorGuard<SnapState, string>("courseId", kind),
  contentId: actionCreatorGuard<SnapState, string>("contentId", kind),
  studentTotal: actionCreatorGuard<SnapState, StudentTotal>(
    "studentTotal",
    kind
  ),
  snapId: actionCreatorGuard<SnapState, string>("snapId", kind),
  schoolYear: actionCreatorGuard<SnapState, number>("schoolYear", kind),
  release: actionCreatorGuard<SnapState, number>("release", kind),
  seed: actionCreatorGuard<SnapState, number>("seed", kind),
  completed: actionCreatorGuard<SnapState, boolean>("completed", kind),
  completed_timestamp: actionCreatorGuard<
    SnapState,
    FirestoreTimestampType | null
  >("completed_timestamp", kind),
  snapPaths: actionCreatorGuard<SnapState, Array<SnapPath>>("snapPaths", kind),
  __reset: actionCreatorTypeGuard<TypeArray, Partial<SnapState> | null>(
    "reset",
    "__reset/SnapState"
  )
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Omit<SnapState, "kind" | "version">
    | "__reset"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const snapStateAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

export const snapStateReducer = reducerGuard<SnapState>(kind)
  .case(...reducerAction.meta)
  .case(...reducerAction.snapPaths)
  .case(...reducerAction.screenStates)
  .case(...reducerAction.screenIdIndex)
  .case(...reducerAction.seed)
  .case(...reducerAction.courseId)
  .case(...reducerAction.contentId)
  .case(...reducerAction.studentTotal)
  .case(...reducerAction.snapId)
  .case(...reducerAction.schoolYear)
  .case(...reducerAction.release)
  .case(snapStateAction.completed, (state, completed) => ({
    ...state,
    completed: completed,
    completed_timestamp: firebase.firestore.Timestamp.now()
  }))
  .case(...reducerAction.completed_timestamp)
  .case(
    snapStateAction.__reset,
    (_state, newState) => ({ ...newState } as SnapState)
  )
  .default((state, action: any) => {
    action = processActionMeta(action)
    let prevState = _.cloneDeep(state)

    const {
      prop,
      idx,
      nodes
    }: { prop: keyof SnapState; idx: number; nodes: Array<any> } = action?.meta
    if (prop == "screenStates") {
      if (prevState?.[prop]?.length < idx + 1) {
        prevState[prop][idx] = ScreenStateSchema.cast({}, validationOptions)
      }
    }

    switch (action?.meta?.prop) {
      case "screenStates":
        return getArrayReducerGuard<SnapState, ScreenState>(
          kind,
          "screenStates",
          "ScreenState",
          prevState,
          action
        )
      case "snapPaths":
        return getArrayReducerGuard<SnapState, SnapPath>(
          kind,
          "snapPaths",
          "SnapPath",
          prevState,
          action
        )
    }

    return prevState
  })
